import _orderBy from "lodash-es/orderBy";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import { useNewSidebarExperience } from "featureFlags/hooks/useNewSidebarExperience";
import {
  WorkspaceEngine,
  WorkspaceEngineStatus,
  WorkspaceEngineType,
} from "services/engines/engine.types";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";
import { PinnedItemType, usePinnedItems } from "services/pinnedItems";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";

import { useAccessManager } from "components/App/accessManager";
import { useLayoutToggles } from "components/LayoutToggles/context";

import { useCurrentAccount } from "../../Account/useCurrentAccount";
import { useCreateEngine } from "../CreateMenu/useCreateEngine";
import { EmptySearch } from "../EmptyState/EmptySearch";
import { EmptyState, EngineIcon } from "../EmptyState/EmptyState";
import { Search } from "../Search";
import { useLeftSidebarContext } from "../context";
import { CollapsedPanel } from "./CollapsedPanel";
import { useDeleteEngine } from "./EngineRow/useDeleteEngine";
import { useEditEngine } from "./EngineRow/useEditEngine";
import { ExpandedPanel } from "./ExpandedPanel";
import { OrderBy, OrderDirection } from "./types";

const workspaceEngineStatusOrder = {
  [WorkspaceEngineStatus.STOPPED]: 1,
  [WorkspaceEngineStatus.STOPPING]: 2,
  [WorkspaceEngineStatus.DRAINING]: 3,

  [WorkspaceEngineStatus.RESIZING]: 4,

  [WorkspaceEngineStatus.STARTING]: 5,
  [WorkspaceEngineStatus.RUNNING]: 6,
};

const workspaceEngineTypeOrder = {
  [WorkspaceEngineType.None]: 1,
  [WorkspaceEngineType.XS]: 2,
  [WorkspaceEngineType.S]: 3,
  [WorkspaceEngineType.M]: 4,
  [WorkspaceEngineType.L]: 5,
  [WorkspaceEngineType.XL]: 6,
};

export const EnginesPanel = React.memo(() => {
  const { t } = useTranslation();
  const enabledGcpAccount = useEnableGcpAccount();
  const newSidebarExperience = useNewSidebarExperience();
  const { onEngineSearchChange, engineSearchState } = useLeftSidebarContext();
  const { data: engines } = useWorkspaceEngines();
  const { createEngineMarkup, openCreateEngine } = useCreateEngine();
  const { isAllowedTo } = useAccessManager();
  const { openEditEngine, editEngineMarkup } = useEditEngine();
  const { openDeleteEngine, deleteEngineMarkup } = useDeleteEngine();
  const { getAccount } = useCurrentAccount();
  const { pinnedItems, togglePinned } = usePinnedItems(PinnedItemType.ENGINE);
  const [order, setOrder] = useState<{
    orderDirection: OrderDirection;
    orderBy: OrderBy;
  }>({
    orderDirection: OrderDirection.ASC,
    orderBy: OrderBy.name,
  });

  const { layout } = useLayoutToggles();

  const filtered = (engines || []).filter(engine => {
    return engine.engineName
      .toLowerCase()
      .includes(engineSearchState.toLowerCase());
  });

  const orderKey = (engine: WorkspaceEngine) => {
    if (order.orderBy === OrderBy.type) {
      const weight = workspaceEngineTypeOrder[engine.type];
      return weight;
    }
    if (order.orderBy === OrderBy.status) {
      const weight = workspaceEngineStatusOrder[engine.status];
      return weight;
    }
    if (order.orderBy === OrderBy.clusters) {
      return Number(engine.clusters);
    }
    if (order.orderBy === OrderBy.nodes) {
      return Number(engine.nodes);
    }
    return engine.engineName.toLowerCase();
  };

  const sorted = _orderBy(
    filtered,
    newSidebarExperience
      ? [
          (engine: WorkspaceEngine) =>
            engine.status === WorkspaceEngineStatus.RUNNING ? 0 : 1,
          orderKey,
        ]
      : [orderKey, "engineName"],
    newSidebarExperience
      ? [OrderDirection.ASC, order.orderDirection]
      : [order.orderDirection]
  );

  const { pinned, unpinned } = useMemo(() => {
    if (!newSidebarExperience) {
      return { pinned: [], unpinned: sorted };
    }
    const pinnedSet = new Set(pinnedItems);
    return {
      pinned: sorted.filter(engine => pinnedSet.has(engine.engineName)),
      unpinned: sorted.filter(engine => !pinnedSet.has(engine.engineName)),
    };
  }, [newSidebarExperience, pinnedItems, sorted]);

  const allowedToCreateEngine =
    isAllowedTo(
      PrivilegeObjectType.account,
      getAccount().accountName,
      SqlRbacAction.ENGINE_CREATE
    ) && !enabledGcpAccount;

  const handleChangeOrder = (orderParams: { orderBy: OrderBy }) => {
    setOrder(current => {
      if (current.orderBy === orderParams.orderBy) {
        return {
          ...orderParams,
          orderDirection:
            current.orderDirection === OrderDirection.ASC
              ? OrderDirection.DESC
              : OrderDirection.ASC,
        };
      }
      return {
        ...orderParams,
        orderDirection: OrderDirection.ASC,
      };
    });
  };

  if (!engines?.length) {
    return (
      <>
        <Search
          onChange={onEngineSearchChange}
          value={engineSearchState}
          testId="engines-search-input"
          disabled
        />
        <EmptyState
          icon={<EngineIcon />}
          title={t("workspace.engines.empty")}
          subtitle={
            allowedToCreateEngine
              ? t("workspace.engines.empty_subtitle")
              : undefined
          }
          button={
            allowedToCreateEngine
              ? t("workspace.engines.empty_button")
              : undefined
          }
          onClick={openCreateEngine}
          testId="engines-empty-list"
        />
        {createEngineMarkup}
      </>
    );
  }

  if (engineSearchState && !filtered.length) {
    return (
      <>
        <Search
          onChange={onEngineSearchChange}
          value={engineSearchState}
          testId="engines-search-input"
        />
        <EmptySearch
          search={engineSearchState}
          testId="empty-engines-search"
        />
      </>
    );
  }

  const actions = {
    openEditEngine,
    openDeleteEngine,
  };

  return (
    <>
      <Search
        onChange={onEngineSearchChange}
        value={engineSearchState}
        testId="engines-search-input"
      />
      {layout.engines?.expanded ? (
        <ExpandedPanel
          items={newSidebarExperience ? unpinned : sorted}
          pinnedItems={newSidebarExperience ? pinned : undefined}
          pinnedLabel={t("workspace.engines.pinned")}
          onChangeOrder={handleChangeOrder}
          order={order}
          actions={actions}
          onTogglePin={newSidebarExperience ? togglePinned : undefined}
          pinnedEngineNames={
            newSidebarExperience ? new Set(pinnedItems) : undefined
          }
          testId="engines-expanded-list-panel"
        />
      ) : (
        <CollapsedPanel
          items={newSidebarExperience ? unpinned : sorted}
          pinnedItems={newSidebarExperience ? pinned : undefined}
          pinnedLabel={t("workspace.engines.pinned")}
          actions={actions}
          onTogglePin={newSidebarExperience ? togglePinned : undefined}
          pinnedEngineNames={
            newSidebarExperience ? new Set(pinnedItems) : undefined
          }
          testId="engines-list-panel"
        />
      )}
      {editEngineMarkup}
      {deleteEngineMarkup}
    </>
  );
});

export default EnginesPanel;
